import { useState } from 'react';
import { AlertTriangle, Bell, CheckCircle, Download, Plus, Receipt, X } from 'lucide-react';
import { PageShell } from '@/components/layout/PageShell';
import { EmptyState } from '@/components/ui/EmptyState';
import { EntryModal } from '@/components/financial/EntryModal';
import { usePayables, type FullPayableRow } from '@/hooks/usePayablesReceivables';
import { formatBRL, formatDate } from '@/lib/format';
import { downloadCsv } from '@/lib/export';
import { cn } from '@/lib/utils';

type Filter = 'pending' | 'paid' | 'cancelled' | 'all';

const FILTER_LABELS: Record<Filter, string> = {
  pending:   'Pendentes',
  paid:      'Pagas',
  cancelled: 'Canceladas',
  all:       'Todas',
};

function statusChip(status: FullPayableRow['status']) {
  switch (status) {
    case 'overdue':    return <span className="chip-negative">Atrasada</span>;
    case 'pending':    return <span className="chip-warning">Pendente</span>;
    case 'paid':       return <span className="chip-positive">Paga</span>;
    case 'cancelled':  return <span className="chip-neutral">Cancelada</span>;
  }
}

export function Payables() {
  const { data, loading, fetchError, createPayable, markPaid, cancelPayable } = usePayables();
  const [filter, setFilter] = useState<Filter>('pending');
  const [showModal, setShowModal] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');

  const filtered = data.filter((r) => {
    if (filter === 'all') return true;
    if (filter === 'pending') return r.status === 'pending' || r.status === 'overdue';
    return r.status === filter;
  });

  const total = filtered.reduce((s, r) => s + r.amount_cents, 0);

  async function handleMarkPaid(id: string) {
    setActing(id);
    setActionError('');
    try { await markPaid(id); } catch (e: any) { setActionError(e.message); } finally { setActing(null); }
  }

  async function handleCancel(id: string) {
    setActing(id);
    setConfirming(null);
    setActionError('');
    try { await cancelPayable(id); } catch (e: any) { setActionError(e.message); } finally { setActing(null); }
  }

  const overdueCount = data.filter((r) => r.status === 'overdue').length;

  const today   = new Date(); today.setHours(0, 0, 0, 0);
  const in7days = new Date(today); in7days.setDate(today.getDate() + 7);
  const upcoming = data.filter((r) => {
    if (r.status !== 'pending') return false;
    const d = new Date(r.due_date); d.setHours(12, 0, 0, 0);
    return d >= today && d <= in7days;
  });
  const upcomingTotal = upcoming.reduce((s, r) => s + r.amount_cents, 0);

  function handleExport() {
    const headers = ['Fornecedor', 'Descrição', 'Vencimento', 'Valor (R$)', 'Status'];
    const rows = filtered.map((r) => [
      r.payee_name,
      r.description,
      r.due_date,
      (r.amount_cents / 100).toFixed(2).replace('.', ','),
      r.status === 'overdue' ? 'Atrasada' : r.status === 'pending' ? 'Pendente' : r.status === 'paid' ? 'Paga' : 'Cancelada',
    ]);
    downloadCsv(`contas_a_pagar_${new Date().toISOString().slice(0,10)}.csv`, headers, rows);
  }

  return (
    <PageShell
      title="Contas a pagar"
      subtitle="Pagamentos programados, em atraso e histórico de pagamentos."
      back="/financeiro"
      maxWidth="xl"
    >
      {/* Header actions */}
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        {/* Filter tabs */}
        <div className="flex gap-1 bg-surface-subtle rounded-xl p-1">
          {(Object.keys(FILTER_LABELS) as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors relative',
                filter === f
                  ? 'bg-surface text-ink shadow-sm'
                  : 'text-ink-muted hover:text-ink'
              )}
            >
              {FILTER_LABELS[f]}
              {f === 'pending' && overdueCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-negative text-[10px] text-white font-bold">
                  {overdueCount}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {filtered.length > 0 && (
            <button onClick={handleExport} className="btn-secondary !py-1.5 gap-1.5 !text-xs">
              <Download className="w-3.5 h-3.5" />Exportar CSV
            </button>
          )}
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary gap-1.5"
          >
            <Plus className="w-4 h-4" />
            Nova conta
          </button>
        </div>
      </div>

      {/* Fetch / action errors */}
      {(fetchError || actionError) && (
        <div className="mb-3 flex items-start justify-between gap-3 rounded-xl border border-negative/30 bg-negative/5 px-4 py-3 text-sm text-negative">
          <span>{fetchError || actionError}</span>
          <button onClick={() => setActionError('')} className="shrink-0 text-negative/60 hover:text-negative">✕</button>
        </div>
      )}

      {/* Alert: overdue */}
      {filter === 'pending' && overdueCount > 0 && (
        <div className="mb-3 rounded-xl bg-negative/8 border border-negative/20 px-4 py-3 text-sm text-negative font-medium flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {overdueCount} {overdueCount === 1 ? 'conta atrasada' : 'contas atrasadas'} — vencimento já passou.
        </div>
      )}

      {/* Próximos 7 dias */}
      {filter === 'pending' && upcoming.length > 0 && (
        <div className="mb-4 rounded-xl bg-warning/8 border border-warning/20 px-4 py-3 text-sm text-warning flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 shrink-0" />
            <span className="font-medium">
              {upcoming.length} {upcoming.length === 1 ? 'pagamento vence' : 'pagamentos vencem'} nos próximos 7 dias
            </span>
          </div>
          <span className="tabular-nums font-semibold shrink-0">{formatBRL(upcomingTotal)}</span>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-2xl bg-surface-subtle animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-8">
          <EmptyState
            icon={Receipt}
            title={
              filter === 'pending'
                ? 'Nenhuma conta a pagar pendente'
                : filter === 'paid'
                ? 'Nenhum pagamento registrado'
                : filter === 'cancelled'
                ? 'Nenhum lançamento cancelado'
                : 'Nenhuma conta cadastrada'
            }
            description={filter === 'pending' ? 'Clique em "Nova conta" para registrar um lançamento manualmente.' : undefined}
            action={
              filter === 'pending' ? (
                <button onClick={() => setShowModal(true)} className="btn-primary gap-1.5">
                  <Plus className="w-4 h-4" />
                  Nova conta
                </button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-ink-muted border-b border-border bg-surface-subtle/60">
                <th className="px-5 py-3 font-medium">Fornecedor</th>
                <th className="px-4 py-3 font-medium">Descrição</th>
                <th className="px-4 py-3 font-medium">Vencimento</th>
                <th className="px-4 py-3 font-medium text-right">Valor</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const isActive = r.status === 'pending' || r.status === 'overdue';
                return (
                  <tr
                    key={r.id}
                    className="border-b border-border last:border-0 hover:bg-surface-subtle/40 transition-colors"
                  >
                    <td className="px-5 py-3 font-medium text-ink">{r.payee_name}</td>
                    <td className="px-4 py-3 text-ink-muted truncate max-w-[220px]">{r.description}</td>
                    <td className={cn(
                      'px-4 py-3 text-sm tabular-nums',
                      r.status === 'overdue' ? 'text-negative font-semibold' : 'text-ink-muted'
                    )}>
                      {formatDate(r.due_date + 'T12:00:00')}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-negative">
                      −{formatBRL(r.amount_cents)}
                    </td>
                    <td className="px-4 py-3">{statusChip(r.status)}</td>
                    <td className="px-5 py-3">
                      {isActive && (
                        <div className="flex items-center gap-1 justify-end">
                          {confirming === r.id ? (
                            <>
                              <span className="text-xs text-ink-muted mr-1">Confirmar?</span>
                              <button
                                onClick={() => handleCancel(r.id)}
                                disabled={acting === r.id}
                                className="text-xs text-negative font-medium hover:underline"
                              >
                                Sim
                              </button>
                              <span className="text-ink-soft text-xs">·</span>
                              <button
                                onClick={() => setConfirming(null)}
                                className="text-xs text-ink-muted hover:underline"
                              >
                                Não
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleMarkPaid(r.id)}
                                disabled={acting === r.id}
                                title="Marcar como paga"
                                className="p-1.5 rounded-lg text-positive hover:bg-positive/10 transition-colors"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setConfirming(r.id)}
                                title="Cancelar lançamento"
                                className="p-1.5 rounded-lg text-ink-soft hover:bg-surface-subtle hover:text-negative transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="bg-surface-subtle/60 border-t border-border">
                  <td colSpan={3} className="px-5 py-3 text-sm font-semibold text-ink">
                    Total ({filtered.length} {filtered.length === 1 ? 'lançamento' : 'lançamentos'})
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold text-negative">
                    −{formatBRL(total)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {showModal && (
        <EntryModal
          mode="payable"
          onClose={() => setShowModal(false)}
          onSubmit={createPayable}
        />
      )}
    </PageShell>
  );
}
